import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BadgeCheck, ClipboardList, QrCode, ShieldCheck, Users, KanbanSquare } from "lucide-react";
import { BrandMark } from "@/components/Brand";
import { HeroCarousel } from "@/components/HeroCarousel";
import { LogoutButton } from "@/components/LogoutButton";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";

const FEATURES = [
  { icon: QrCode, title: "Scan once, capture consented leads", text: "Visitors scan your booth QR code and land on a featherweight page. Name + email, two clear consents, done." },
  { icon: ShieldCheck, title: "Consent you can prove", text: "Every share stores timestamp, vendor, event and consent text version. Marketing opt-in is separate." },
  { icon: ClipboardList, title: "Quote questions at the moment of interest", text: "Your own fields — text, number, select, multi-select, date — answered now or later." },
  { icon: KanbanSquare, title: "A CRM built for booth follow-up", text: "Pipeline from New to Booked, notes, tags, chat, email, SMS & WhatsApp follow-ups, CSV export." },
];

export default function Landing() {
  const { vendor, customer } = useAuth();
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    if (params.get("closed")) {
      toast.success("Your ExpoEventHub account is closed. Email support within 30 days if you change your mind.");
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
        <BrandMark />
        <nav className="flex items-center gap-2">
          {vendor && <Link to="/dashboard" data-testid="nav-dashboard-link"><Button variant="ghost" className="rounded-sm">Dashboard</Button></Link>}
          {customer && <Link to="/me" data-testid="nav-me-link"><Button variant="ghost" className="rounded-sm">My vendors</Button></Link>}
          {(vendor || customer) && <LogoutButton kind={vendor ? "vendor" : "customer"} testid="nav-logout-button" />}
          {!vendor && !customer && <>
          <Link to="/attendee" className="px-2 text-sm font-medium text-slate-600 hover:text-slate-900 sm:px-3" data-testid="nav-attendee-login-link">Attendee login</Link>
          <Link to="/login" data-testid="nav-vendor-login-link"><Button variant="ghost" className="rounded-sm">Vendor login</Button></Link>
          <Link to="/login?mode=signup" data-testid="nav-start-trial-link"><Button className="hard-shadow rounded-sm">Start free trial</Button></Link>
          </>}
        </nav>
      </header>

      <section className="mx-auto grid max-w-6xl gap-10 px-5 pb-16 pt-8 lg:grid-cols-[1.1fr_0.9fr] lg:pt-16">
        <div className="space-y-7">
          <p className="rise text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">For expos · trade shows · wedding fairs</p>
          <h1 className="rise rise-1 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            One scan at your booth.<br />A consented lead in your pipeline.
          </h1>
          <p className="rise rise-2 max-w-xl text-base text-slate-600 sm:text-lg">
            Visitors scan your booth QR code, share their details with explicit consent, and instantly save your brochure, socials and booking link. You follow up from a lightweight CRM.
          </p>
          <div className="rise rise-3 flex flex-wrap gap-3">
            <Link to="/login?mode=signup" data-testid="hero-start-trial-button">
              <Button className="hard-shadow h-12 rounded-sm px-8 text-base transition-transform hover:-translate-y-[1px]">Start 30-day trial <ArrowRight className="ml-1 h-4 w-4" /></Button>
            </Link>
            <Link to="/t/demo-bridal" data-testid="hero-demo-booth-button">
              <Button variant="outline" className="h-12 rounded-sm px-8 text-base">Try a demo booth scan</Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-5 pt-2 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-emerald-600" /> Passwordless email codes</span>
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-emerald-600" /> Shared staff accounts</span>
          </div>
        </div>
        <div className="relative">
          <HeroCarousel />
          <div className="hard-shadow absolute -bottom-6 left-4 right-4 rounded-sm border bg-white/95 p-4 backdrop-blur sm:left-auto sm:right-[-12px] sm:w-72">
            <div className="flex items-center gap-3">
              <span className="relative grid h-10 w-10 place-items-center rounded-sm bg-emerald-50">
                <span className="nfc-ping absolute h-6 w-6 rounded-sm bg-emerald-400/50" />
                <QrCode className="relative h-5 w-5 text-emerald-700" />
              </span>
              <div>
                <p className="text-sm font-bold">New lead · Booth B-24</p>
                <p className="text-xs text-slate-500">Shared with consent · just now</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t bg-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-16 sm:grid-cols-2">
          {FEATURES.map((f) => (
            <div key={f.title} className="hard-shadow-hover space-y-3 rounded-sm border bg-white p-8">
              <div className="grid h-11 w-11 place-items-center rounded-sm bg-secondary"><f.icon className="h-6 w-6 text-white" /></div>
              <h3 className="text-xl font-medium">{f.title}</h3>
              <p className="text-sm text-slate-600">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-20"><div className="rounded-sm bg-secondary text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-6 px-5 py-14 md:flex-row md:items-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-emerald-400">Pro $20 / month · 30-day free trial</p>
            <h2 className="mt-2 text-2xl font-bold tracking-tight sm:text-3xl">Ready for your next show?</h2>
          </div>
          <Link to="/login?mode=signup" data-testid="footer-start-trial-button">
            <Button className="h-12 rounded-sm px-8">Create vendor account</Button>
          </Link>
        </div>
      </div></section>
    </div>
  );
}
